import React, { type ReactElement, type ReactNode, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import KNDetailsPopover from 'components/KNDetailsPopover/KNDetailsPopover'
import KNDetailsPopoverSection from 'components/KNDetailsPopover/KNDetailsPopoverSection'
import usePopoverState from 'components/KNDetailsPopover/usePopoverState'
import { replaceHighlights } from 'global/helpers/highlights'
import type { KNDataTableOverflowProps } from './types'

const itemWrapper = (item: ReactNode) => (
  <Typography
    component="div"
    color="inherit"
    fontSize="0.75rem"
    sx={{
      paddingX: 2,
      paddingY: 0.25,
      lineHeight: '20px',
      '&:hover': {
        backgroundColor: 'primary.dark',
      },
    }}
  >
    {item}
  </Typography>
)

const KNDataTableOverflow = <T extends object>({
  row,
  label,
  header,
  items,
  limit = 1,
  rightAligned,
  withoutWrapper,
  highlights,
  sx,
}: KNDataTableOverflowProps<T>): ReactElement | null => {
  const {
    anchorEl: moreItemsAnchorEl,
    open: moreItemsOpen,
    handleClick: handleMoreItemsClick,
    handleClose: handleMoreItemsClose,
  } = usePopoverState()

  const handlePropagation = useCallback((event: React.MouseEvent) => event.stopPropagation(), [])
  const { t } = useTranslation()

  if (items.length === 0) {
    return <Typography color="primary.light" sx={sx}>{t('general.none')}</Typography>
  }

  const processedItems = highlights?.length
    ? items.map(item => typeof item === 'string' ? replaceHighlights(item, highlights) : item)
    : items

  return (
    <Box onClick={handlePropagation} sx={sx}>
      <Stack spacing={1} direction="row" alignItems="center">
        <Stack data-test="shipmentId" direction="column">
          {processedItems.slice(0, limit).map((item, index) => (
            // don't care about stability here
            // eslint-disable-next-line react/no-array-index-key
            <Box key={index} color="primary.main" >{item}</Box>
          ))}
        </Stack>
        {items.length > limit && (
          <Tooltip title={label} arrow enterDelay={500}>
            <Chip
              data-test="shipmentMoreIcon"
              label={`+${items.length - limit}`}
              size="small"
              onClick={handleMoreItemsClick}
            />
          </Tooltip>
        )}
      </Stack>
      {items.length > limit && (
        <KNDetailsPopover
          open={moreItemsOpen}
          onClose={handleMoreItemsClose}
          anchorEl={moreItemsAnchorEl}
          rightAligned={rightAligned}
        >
          <KNDetailsPopoverSection header={header} items={withoutWrapper ? processedItems : processedItems.map(itemWrapper)} />
        </KNDetailsPopover>
      )}
    </Box>
  )
}

export default KNDataTableOverflow
